/**
 * Round 20 U4 -- the open findings on what a service rests on.
 *
 * Card payments at POS listed its own 2 open findings and not the
 * critical alert on Authorisation Host, which it rests on; the reader
 * had to know the dependency to go and look. Said here, apart from the
 * service's own, newest first.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { api } from "shared/api/client";
import { Card } from "shared/components/Card";
import { i18n } from "shared/i18n";
import { formatDateTime } from "shared/time/formatDate";

interface RestsOnFinding {
  id: string;
  title: string;
  severity: string;
  raised_at: string;
  entity_kind: string;
  entity_name: string;
}

interface RestsOnFindings {
  own: number;
  rests_on: RestsOnFinding[];
}

export function RestsOnFindingsPanel({ ibsId }: { ibsId: string | undefined }) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["ibs-rests-on-findings", ibsId],
    queryFn: () =>
      api.get<RestsOnFindings>(`/v1/twin/ibses/${ibsId}/rests-on-findings`),
    enabled: Boolean(ibsId),
  });
  if (!q.data || q.data.rests_on.length === 0) return null;
  const critical = q.data.rests_on.filter((f) => f.severity === "critical");
  return (
    <Card testid="ibs-rests-on-findings" title={t("restsOn.heading")} icon="finding">
      <p data-testid="ibs-rests-on-summary" className="text-sm text-slate-700">
        {t("restsOn.summary", {
          own: q.data.own,
          count: q.data.rests_on.length,
          critical: critical.length,
        })}
      </p>
      <ul className="mt-1 space-y-0.5 text-xs text-slate-700">
        {q.data.rests_on.map((f) => (
          <li key={f.id} data-testid={`ibs-rests-on-${f.id}`}>
            <span
              className={
                f.severity === "critical"
                  ? "font-semibold text-rose-700"
                  : "text-slate-600"
              }
            >
              {t(`board.severity.${f.severity}`, { defaultValue: f.severity })}
            </span>{" "}
            <Link
              to={`/twin/findings/${f.id}`}
              className="text-brand-brassDark hover:underline"
            >
              {f.title}
            </Link>{" "}
            {t("restsOn.onWhat", {
              name: f.entity_name,
              since: formatDateTime(f.raised_at, i18n.language),
            })}
          </li>
        ))}
      </ul>
    </Card>
  );
}
